import {createAsyncThunk, isAnyOf} from '@reduxjs/toolkit'

import {
    BankCodeType,
    CardCodeType,
    EasyCardItemTypeType,
    NonInstallment,
    NormalPaymentStatus,
    PaymentMethodEntry,
    PaymentMethodEntryType,
    PaymentsCardCodeType,
    PaymentTabMethodType,
    PayMethod,
    PayMethodType,
    RechargePaymentStatusType,
    SelectableRechargePaymentStatus,
} from '@web/core/constants'
import {
    paymentMethodInfoSelector,
    rechargeablePointAmountSelector,
    rechargePaymentInfoSelector,
} from '@web/selectors/paymentMethodInfo'
import {selectedRechargeAccountKeySelector} from '@web/slices/account/slice'
import {chargePointSelector} from '@web/slices/chargePoint/slice'
import {payLaterSelector} from '@web/slices/payLater/slice'
import {paymentSelector} from '@web/slices/paymentMethod/slice'

import {RootState, SelectPayMethodActionPayload, ThunkApi} from './interfaces'

import type {AsyncThunk} from '@reduxjs/toolkit'

type BaseActionArgType = {
    entry?: PaymentMethodEntryType
    selectedPayMethodTab?: Maybe<PaymentTabMethodType>
}
type BaseReturnType = BaseActionArgType &
    SelectPayMethodActionPayload & {
        currentPayMethods: PayMethodType[]
    }

export type PaymentMethodActionType = AsyncThunk<BaseReturnType, BaseActionArgType, ThunkApi>

const makeSelectPayMethodAction = <Arg extends object>({
    selectedPayMethod,
    before,
}: {
    selectedPayMethod: PayMethodType
    before?: (state: RootState) => string | 'NORMAL'
}): AsyncThunk<BaseReturnType & Arg, BaseActionArgType & Arg, ThunkApi> => {
    type ActionArgType = BaseActionArgType & Arg
    type ActionReturnType = BaseReturnType & Arg
    return createAsyncThunk<ActionReturnType, ActionArgType, ThunkApi>(
        `selectPayMethod/${selectedPayMethod}`,
        async (args, {getState, rejectWithValue}) => {
            const state = getState()
            const {selectedPayMethodTab} = args

            const {
                recharging: {amount: currentRechargingPointAmount},
            } = chargePointSelector(state)

            const {status, payMethods: currentPayMethods, tabStatus, complexEnabled} = paymentMethodInfoSelector(state)

            const beforeStatus = before?.(state) ?? 'NORMAL'

            if (status !== 'NORMAL') {
                return rejectWithValue(status)
            }

            if (selectedPayMethodTab && tabStatus[selectedPayMethodTab] !== 'NORMAL') {
                return rejectWithValue(tabStatus[selectedPayMethodTab])
            }

            if (currentPayMethods.includes(selectedPayMethod)) {
                return rejectWithValue('NONE')
            }

            if (beforeStatus !== 'NORMAL') {
                return rejectWithValue(beforeStatus)
            }

            /**
             * 복합결제수단 변경
             * - 복합결제는 포인트 충전결제수단과 다른 결제수단 (계좌간편, 카드간편, 일반결제)으로 이루어진다.
             * 1. 복합결제는 이전결제 수단을 해제할 수 있다.
             * 2. 이때 충전결제를 해제할 경우 선택한 다른 결제수단으로 단일 결제수단이 된다.
             * 3. 복합결제 상태에서 충전결제를 제외한 다른 결제수단을 선택하면, 충전결제는 유지되고, 결제수단이 변경된다.
             * 4. 복합결제 상태에서 모든 결제수단을 초기화하면, 우선순위에 따른 결제수단이 선택된다. (마지막에 충전결제를 해제)
             **/
            const nextPayMethods: PayMethodType[] =
                complexEnabled && selectedPayMethod !== PayMethod.FAMILY_PAY
                    ? [PayMethod.RECHARGE_POINT, selectedPayMethod]
                    : [selectedPayMethod]

            return {
                ...args,
                payMethods: nextPayMethods,
                currentPayMethods,
                selectedPayMethod,
                rechargingPointAmount:
                    selectedPayMethod === PayMethod.RECHARGE_POINT
                        ? rechargeablePointAmountSelector(state)
                        : nextPayMethods.includes(PayMethod.RECHARGE_POINT)
                          ? currentRechargingPointAmount
                          : 0,
            }
        },
    )
}

/** 간편 계좌 선택 */
export const selectEasyAccountPayMethod: AsyncThunk<
    BaseReturnType & {accountKey?: string},
    BaseActionArgType & {accountKey?: string},
    ThunkApi
> = makeSelectPayMethodAction<{
    accountKey?: string
}>({
    selectedPayMethod: PayMethod.BANK_EASY_PAY,
})

/** 카드 선택 */
export const selectCardPayMethod: AsyncThunk<
    BaseReturnType & {type?: CardCodeType; companyCode?: Maybe<PaymentsCardCodeType>; installment?: string},
    BaseActionArgType & {type?: CardCodeType; companyCode?: Maybe<PaymentsCardCodeType>; installment?: string},
    ThunkApi
> = makeSelectPayMethodAction<{
    /** 카드 구분 개인카드 | 법인카드 */
    type?: CardCodeType
    /** 선택된 카드사 코드 */
    companyCode?: Maybe<PaymentsCardCodeType>
    /** 할부 기간 */
    installment?: string
}>({
    selectedPayMethod: PayMethod.CARD,
})

/** 간편 카드 선택 */
export const selectCardEasyPayPayMethod: AsyncThunk<
    BaseReturnType & {id?: Maybe<string>; installment?: string; itemType?: EasyCardItemTypeType},
    BaseActionArgType & {id?: Maybe<string>; installment?: string; itemType?: EasyCardItemTypeType},
    ThunkApi
> = makeSelectPayMethodAction<{
    /** 선택된 카드 간편결제 카드 ID */
    id?: Maybe<string>
    /** 선택된 카드 할부기간 */
    installment?: string
    /** 카드 아이템 타입 */
    itemType?: EasyCardItemTypeType
}>({
    selectedPayMethod: PayMethod.CARD_EASY_PAY,
})

/** 충전 결제 선택 */
export const selectRechargePointPayMethod: AsyncThunk<
    BaseReturnType & {accountKey?: Maybe<string>},
    BaseActionArgType & {accountKey?: Maybe<string>},
    ThunkApi
> = makeSelectPayMethodAction<{
    accountKey?: Maybe<string>
}>({
    selectedPayMethod: PayMethod.RECHARGE_POINT,
    before: (state): RechargePaymentStatusType => {
        const {status: rechargeStatus} = rechargePaymentInfoSelector(state)

        if (!SelectableRechargePaymentStatus.includes(rechargeStatus)) {
            return rechargeStatus
        }
        return 'NORMAL'
    },
})

/** 모바일 결제 선택 */
export const selectMobilePayMethodNext: AsyncThunk<
    BaseReturnType & {mobileNumbers?: string},
    BaseActionArgType & {mobileNumbers?: string},
    ThunkApi
> = makeSelectPayMethodAction<{
    mobileNumbers?: string
}>({
    selectedPayMethod: PayMethod.MOBILE,
})

/** 간편 모바일 결제 선택 */
export const selectMobileEasyPayPayMethod: AsyncThunk<
    BaseReturnType & {mobileNumbers?: string; pinNo?: string},
    BaseActionArgType & {mobileNumbers?: string; pinNo?: string},
    ThunkApi
> = makeSelectPayMethodAction<{
    mobileNumbers?: string
    pinNo?: string
}>({
    selectedPayMethod: PayMethod.MOBILE_EASY_PAY,
})

/** 문화누리카드 결제 선택 */
export const selectWelfareCardPayMethod: AsyncThunk<BaseReturnType, BaseActionArgType, ThunkApi> =
    makeSelectPayMethodAction({
        selectedPayMethod: PayMethod.WELFARE_CARD,
    })

/** 컬쳐랜드 결제 선택 */
export const selectCultureLandPayMethod: AsyncThunk<BaseReturnType, BaseActionArgType, ThunkApi> =
    makeSelectPayMethodAction({
        selectedPayMethod: PayMethod.CULTURE_LAND,
    })

/** 해피머니 결제 선택 */
export const selectHappyMoneyPayMethod: AsyncThunk<BaseReturnType, BaseActionArgType, ThunkApi> =
    makeSelectPayMethodAction({
        selectedPayMethod: PayMethod.HAPPY_MONEY,
    })

/** 티머니 결제 선택 */
export const selectMoneyTokenPayMethod: AsyncThunk<BaseReturnType, BaseActionArgType, ThunkApi> =
    makeSelectPayMethodAction({
        selectedPayMethod: PayMethod.MONEY_TOKEN,
    })

/** PayPal 결제 선택 */
export const selectPayPalPayMethod: AsyncThunk<BaseReturnType, BaseActionArgType, ThunkApi> = makeSelectPayMethodAction(
    {
        selectedPayMethod: PayMethod.PAY_PAL,
    },
)

/** 무통장입금 결제 선택 */
export const selectVirtualAccountPayMethod: AsyncThunk<
    BaseReturnType & {code?: Maybe<BankCodeType>},
    BaseActionArgType & {code?: Maybe<BankCodeType>},
    ThunkApi
> = makeSelectPayMethodAction<{
    /** 선택된 무통장 입금 은행 code  */
    code?: Maybe<BankCodeType>
}>({
    selectedPayMethod: PayMethod.VIRTUAL_ACCOUNT,
    before: (
        state,
    ): typeof NormalPaymentStatus.PAY_LATER_IMPOSSIBLE_USE_VIRTUAL_ACCOUNT | typeof NormalPaymentStatus.NORMAL => {
        const {enabled: payLaterUsed} = payLaterSelector(state)
        if (payLaterUsed) {
            return NormalPaymentStatus.PAY_LATER_IMPOSSIBLE_USE_VIRTUAL_ACCOUNT
        }
        return NormalPaymentStatus.NORMAL
    },
})

/** 삼성페이 결제 선택 */
export const selectSamsungPayPayMethod: AsyncThunk<BaseReturnType, BaseActionArgType, ThunkApi> =
    makeSelectPayMethodAction({
        selectedPayMethod: PayMethod.SAMSUNG_PAY,
    })

/** 애플페이 결제 선택 */
export const selectApplePayPayMethod: AsyncThunk<BaseReturnType, BaseActionArgType, ThunkApi> =
    makeSelectPayMethodAction({
        selectedPayMethod: PayMethod.APPLE_PAY,
    })

/** 패밀리 결제 선택 */
export const selectFamilyPayPayMethod: AsyncThunk<
    BaseReturnType & {memo?: string},
    BaseActionArgType & {memo?: string},
    ThunkApi
> = makeSelectPayMethodAction<{
    memo?: string
}>({
    selectedPayMethod: PayMethod.FAMILY_PAY,
})

/** 결제수단 선택 해제 */
export const unselectPayMethod = createAsyncThunk<
    BaseReturnType & {
        unselectedPayMethod: PayMethodType
    },
    {
        entry: PaymentMethodEntryType
        unselectedPayMethod: PayMethodType
    },
    ThunkApi
>(`paymentMethod/unselectPayMethod`, async ({entry, unselectedPayMethod}, {getState}) => {
    const state = getState()

    const {
        recharging: {amount: currentRechargingPointAmount},
    } = chargePointSelector(state)

    const {payMethods: currentPayMethods} = paymentMethodInfoSelector(state)

    const nextPayMethods = currentPayMethods.filter((tabMethod: PayMethodType) => tabMethod !== unselectedPayMethod)
    // 결제 해제
    return {
        entry,
        payMethods: nextPayMethods,
        currentPayMethods,
        unselectedPayMethod,
        selectedPayMethod: null,
        rechargingPointAmount: nextPayMethods.includes(PayMethod.RECHARGE_POINT) ? currentRechargingPointAmount : 0,
    }
})

/** 결제수단 상태 업데이트 */
export const updatePaymentMethod = createAsyncThunk<BaseReturnType, void, ThunkApi>(
    `paymentMethod/updatePaymentMethod`,
    async (_, {getState}) => {
        const state = getState()

        const {
            recharging: {amount: currentRechargingPointAmount},
        } = chargePointSelector(state)
        const {payMethods: currentPayMethods} = paymentMethodInfoSelector(state)
        const {
            selected: {prevPayMethod},
        } = paymentSelector(state)
        const {isNeedSelectedPayMethod} = paymentMethodInfoSelector(getState())
        const {status: rechargeStatus, complex} = rechargePaymentInfoSelector(state)

        const nextPayMethods = [...currentPayMethods, ...(isNeedSelectedPayMethod ? [prevPayMethod] : [])].filter(
            (payMethod) => {
                return !(
                    payMethod === PayMethod.RECHARGE_POINT &&
                    !(SelectableRechargePaymentStatus.includes(rechargeStatus) && complex.available)
                )
            },
        )

        return {
            entry: 'INITIALIZE',
            currentPayMethods,
            payMethods: nextPayMethods,
            selectedPayMethod: nextPayMethods.find((payMethod) => payMethod !== PayMethod.RECHARGE_POINT) ?? null,
            rechargingPointAmount: nextPayMethods.includes(PayMethod.RECHARGE_POINT) ? currentRechargingPointAmount : 0,
        }
    },
)

/**
 * 복합결제 없이 단일결제수단만이 선택 될 때 사용
 * ⚠️ 복합결제 또는 할부 포함된 결제 flow에는 사용금지
 * */
export const selectSinglePaymentMethod = createAsyncThunk<
    BaseReturnType & {id?: string; accountKey?: string; installment: string},
    | {payMethod: PayMethodType}
    | {payMethod: typeof PayMethod.RECHARGE_POINT}
    | {payMethod: typeof PayMethod.CARD_EASY_PAY; id: string; installment: string}
    | {payMethod: typeof PayMethod.BANK_EASY_PAY; accountKey: string},
    ThunkApi
>(`paymentMethod/selectSinglePaymentMethod`, async (args, {getState}) => {
    const {payMethod, ...restArgs} = args
    const state = getState()
    const {
        recharging: {amount: currentRechargingPointAmount},
    } = chargePointSelector(state)
    const selectedRechargeAccountKey = selectedRechargeAccountKeySelector(state)

    const isChargePointSelected = payMethod === PayMethod.RECHARGE_POINT

    return {
        entry: PaymentMethodEntry.STAY_CURRENT,
        payMethods: [payMethod],
        currentPayMethods: [payMethod],
        selectedPayMethod: payMethod,
        ...(isChargePointSelected
            ? {
                  rechargingPointAmount: isChargePointSelected ? currentRechargingPointAmount : 0,
                  accountKey: selectedRechargeAccountKey,
              }
            : {
                  /** 다른 충전결제/페이머니 외의 결제수단 선택시 충전포인트를 초기화한다 */
                  rechargingPointAmount: 0,
              }),
        installment: 'installment' in args ? args.installment : NonInstallment,
        ...restArgs,
    }
})
export const SelectedPayMethodActionMap: Record<PayMethodType, Maybe<PaymentMethodActionType>> = {
    [PayMethod.BANK_EASY_PAY]: selectEasyAccountPayMethod,
    [PayMethod.CARD]: selectCardPayMethod,
    [PayMethod.CARD_EASY_PAY]: selectCardEasyPayPayMethod,
    [PayMethod.RECHARGE_POINT]: selectRechargePointPayMethod,
    [PayMethod.MOBILE]: selectMobilePayMethodNext,
    [PayMethod.MOBILE_EASY_PAY]: selectMobileEasyPayPayMethod,
    [PayMethod.WELFARE_CARD]: selectWelfareCardPayMethod,
    [PayMethod.CULTURE_LAND]: selectCultureLandPayMethod,
    [PayMethod.HAPPY_MONEY]: selectHappyMoneyPayMethod,
    [PayMethod.MONEY_TOKEN]: selectMoneyTokenPayMethod,
    [PayMethod.PAY_PAL]: selectPayPalPayMethod,
    [PayMethod.VIRTUAL_ACCOUNT]: selectVirtualAccountPayMethod,
    [PayMethod.SAMSUNG_PAY]: selectSamsungPayPayMethod,
    [PayMethod.FAMILY_PAY]: selectFamilyPayPayMethod,
    [PayMethod.APPLE_PAY]: selectApplePayPayMethod,
    [PayMethod.SKIP]: null,
    [PayMethod.NAVERPAY_POINT]: null,
    [PayMethod.REWARD_POINT]: null,
    [PayMethod.CHARGE_POINT]: null,
    [PayMethod.PAY_LATER]: null,
    [PayMethod.NONE]: null,
    [PayMethod.BANK]: null,
}

/** 결제수단에 대한 action */
export const getSelectedPayMethodAction = ({selectedPayMethod}: {selectedPayMethod: Maybe<PayMethodType>}) => {
    return selectedPayMethod ? (SelectedPayMethodActionMap[selectedPayMethod] ?? null) : null
}

export const SelectPaymentMethodActions = [
    selectEasyAccountPayMethod.fulfilled,
    selectCardPayMethod.fulfilled,
    selectCardEasyPayPayMethod.fulfilled,
    selectRechargePointPayMethod.fulfilled,
    selectMobilePayMethodNext.fulfilled,
    selectMobileEasyPayPayMethod.fulfilled,
    selectWelfareCardPayMethod.fulfilled,
    selectCultureLandPayMethod.fulfilled,
    selectHappyMoneyPayMethod.fulfilled,
    selectMoneyTokenPayMethod.fulfilled,
    selectPayPalPayMethod.fulfilled,
    selectVirtualAccountPayMethod.fulfilled,
    selectSamsungPayPayMethod.fulfilled,
    selectApplePayPayMethod.fulfilled,
    selectFamilyPayPayMethod.fulfilled,
    unselectPayMethod.fulfilled,
    updatePaymentMethod.fulfilled,
    selectSinglePaymentMethod.fulfilled,
]

export const isAnyOfSelectPaymentMethodActions = isAnyOf(
    selectEasyAccountPayMethod.fulfilled,
    selectCardPayMethod.fulfilled,
    selectCardEasyPayPayMethod.fulfilled,
    selectRechargePointPayMethod.fulfilled,
    selectMobilePayMethodNext.fulfilled,
    selectMobileEasyPayPayMethod.fulfilled,
    selectWelfareCardPayMethod.fulfilled,
    selectCultureLandPayMethod.fulfilled,
    selectHappyMoneyPayMethod.fulfilled,
    selectMoneyTokenPayMethod.fulfilled,
    selectPayPalPayMethod.fulfilled,
    selectVirtualAccountPayMethod.fulfilled,
    selectSamsungPayPayMethod.fulfilled,
    selectApplePayPayMethod.fulfilled,
    selectFamilyPayPayMethod.fulfilled,
)
